import { IAbstractInitialState } from "../../types/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  TAccordion,
  TContestByPublickUrl,
  TContestByUrl,
  TContests,
} from "../../types/contests";
import {
  contestCardByPublicUrl,
  addApplication,
  getContests,
  addRemoveContestsLike,
  detailByContest,
  favoriteByContest,
  artistsByContest,
  applicationByContest,
  allTracksByContest,
} from "../../api/asyncThuncks/contestsCards";
import {
  TAddApplicationData,
  TConfirmationByMessage,
} from "../../types/application";
import { TAudioTrack } from "../../types/audioTrack";
import getVote from "../../api/asyncThuncks/vote";
import getConfirmationByMessage from "../../api/asyncThuncks/confirmationByMessage";

interface IInitialState extends IAbstractInitialState {
  accordionStateField: boolean;
  contests: TContests | null;
  contestById: TContestByPublickUrl | null;
  contestByUrl: TContestByUrl;
  application: TAddApplicationData;
  сonfirmationByMessage: TConfirmationByMessage;
  voteStatus: number | null;
}

const initialState: IInitialState = {
  loading: "idle",
  accordionStateField: false,
  contests: null,
  contestById: null,
  contestByUrl: {
    details: null,
    favorites: {
      audioTracks: null,
      name: "",
    },
    artists: null,
    audio: null,
    application: null,
  },
  application: {
    defaultFields: [],
    extraFields: [],
  },
  сonfirmationByMessage: {
    phone: "",
    contestId: "",
    audioTrackId: "",
  },
  error: "",
  voteStatus: null,
};

const contestsSlice = createSlice({
  name: "contestsSlice",
  initialState,
  reducers: {
    setAccordionState(state, { payload }: PayloadAction<boolean>) {
      state.accordionStateField = payload;
    },
    setAccordionIsOpen(state, { payload }: PayloadAction<number>) {
      if (state.contestByUrl.application) {
        return {
          ...state,
          contestByUrl: {
            ...state.contestByUrl,
            application: {
              ...state.contestByUrl.application,
              accordions: [
                ...state.contestByUrl.application.accordions.map(
                  (item: TAccordion, i: number) => {
                    if (i === payload) {
                      const newItem = { ...item, isOpen: !item.isOpen };
                      return newItem;
                    } else {
                      const newItem = { ...item, isOpen: false };
                      return newItem;
                    }
                  }
                ),
              ],
            },
          },
        };
      }
      return {
        ...state,
      };
    },
    setDefaultFieldsState(state, { payload }: PayloadAction<any>) {
      const key = Object.keys(payload).map((item: string) => {
        const objectValue = { fieldId: item, value: payload[item] };
        return objectValue;
      });
      state.application = { ...state.application, defaultFields: [...key] };
    },
    setExtraFieldsState(state, { payload }: PayloadAction<any>) {
      const key = Object.keys(payload).map((item: string) => {
        const objectValue = { fieldId: item, value: payload[item] };
        return objectValue;
      });
      state.application = { ...state.application, extraFields: [...key] };
    },
    setPlaylistButtonState(
      state,
      { payload: { id } }: PayloadAction<{ id: string }>
    ) {
      state.contestById?.playlist.audioTracks.map((track, index) => {
        if (id === "radio") {
          track.buttonStates = "play";
          return track;
        } else {
          return track;
        }
      });
    },
    setConfirmationByMessage(
      state,
      action: PayloadAction<TConfirmationByMessage>
    ) {
      state.сonfirmationByMessage = action.payload;
    },
    resetContestByUrl(state) {
      state.contestByUrl = {
        details: null,
        favorites: {
          audioTracks: null,
          name: "",
        },
        artists: null,
        audio: null,
        application: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(contestCardByPublicUrl.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(
        contestCardByPublicUrl.fulfilled,
        (state, { payload }: PayloadAction<TContestByPublickUrl>) => {
          state.contestById = {
            ...payload,
            accordions: payload.accordions.map((item) => ({
              ...item,
              isOpen: false,
            })),
          };
          state.loading = "idle";
        }
      )
      .addCase(contestCardByPublicUrl.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(addApplication.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(addApplication.fulfilled, (state, { payload }) => {
        state.loading = "idle";
      })
      .addCase(addApplication.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(getContests.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getContests.fulfilled, (state, { payload }) => {
        state.contests = payload;
        state.loading = "idle";
      })
      .addCase(getContests.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(
        addRemoveContestsLike.pending,
        (
          state,
          {
            meta: {
              arg: { audioTrackId },
            },
          }
        ) => {
          state.loading = "pending";

          if (state.contestByUrl.audio) {
            state.contestByUrl = {
              ...state.contestByUrl,
              audio: state.contestByUrl.audio.map((audioTrack) => {
                if (audioTrack.id === audioTrackId) {
                  return {
                    ...audioTrack,
                    isLiked: !audioTrack.isLiked,
                    likes: audioTrack.isLiked
                      ? audioTrack.likes - 1
                      : audioTrack.likes + 1,
                  };
                }
                return audioTrack;
              }),
            };
          }
          if (state.contestByUrl.favorites.audioTracks) {
            state.contestByUrl = {
              ...state.contestByUrl,
              favorites: {
                ...state.contestByUrl.favorites,
                audioTracks: state.contestByUrl.favorites.audioTracks.map(
                  (audioTrack) => {
                    if (audioTrack.id === audioTrackId) {
                      return {
                        ...audioTrack,
                        isLiked: !audioTrack.isLiked,
                        likes: audioTrack.isLiked
                          ? audioTrack.likes - 1
                          : audioTrack.likes + 1,
                      };
                    } else {
                      return audioTrack;
                    }
                  }
                ),
              },
            };
          }
        }
      )
      .addCase(addRemoveContestsLike.fulfilled, (state) => {
        state.loading = "idle";
      })
      .addCase(
        addRemoveContestsLike.rejected,
        (
          state,
          {
            meta: {
              arg: { audioTrackId },
            },
          }
        ) => {
          if (state.contestByUrl.audio) {
            state.contestByUrl = {
              ...state.contestByUrl,
              audio: state.contestByUrl.audio.map((audioTrack) => {
                if (audioTrack.id === audioTrackId) {
                  return {
                    ...audioTrack,
                    isLiked: !audioTrack.isLiked,
                    likes: audioTrack.isLiked
                      ? audioTrack.likes - 1
                      : audioTrack.likes + 1,
                  };
                }
                return audioTrack;
              }),
            };
          }
          if (state.contestByUrl.favorites.audioTracks) {
            state.contestByUrl = {
              ...state.contestByUrl,
              favorites: {
                ...state.contestByUrl.favorites,
                audioTracks: state.contestByUrl.favorites.audioTracks.map(
                  (audioTrack) => {
                    if (audioTrack.id === audioTrackId) {
                      return {
                        ...audioTrack,
                        isLiked: !audioTrack.isLiked,
                        likes: audioTrack.isLiked
                          ? audioTrack.likes - 1
                          : audioTrack.likes + 1,
                      };
                    } else {
                      return audioTrack;
                    }
                  }
                ),
              },
            };
          }
        }
      )
      .addCase(detailByContest.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(detailByContest.fulfilled, (state, { payload }) => {
        state.contestByUrl.details = payload;
        state.loading = "idle";
      })
      .addCase(detailByContest.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(favoriteByContest.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(favoriteByContest.fulfilled, (state, { payload }) => {
        state.contestByUrl.favorites.audioTracks = payload.audioTracks;
        state.contestByUrl.favorites.name = payload.name;
        state.loading = "idle";
      })
      .addCase(favoriteByContest.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.contestByUrl.favorites = { audioTracks: null, name: "" };
        state.loading = "failed";
      })
      .addCase(artistsByContest.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(artistsByContest.fulfilled, (state, { payload }) => {
        state.contestByUrl.artists = [...payload];
        state.loading = "idle";
      })
      .addCase(artistsByContest.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(allTracksByContest.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(allTracksByContest.fulfilled, (state, { payload }) => {
        state.contestByUrl.audio = [...payload];
        state.loading = "idle";
      })
      .addCase(allTracksByContest.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(applicationByContest.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(applicationByContest.fulfilled, (state, { payload }) => {
        state.contestByUrl.application = payload;
        state.loading = "idle";
      })
      .addCase(applicationByContest.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(getVote.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getVote.fulfilled, (state, { payload }) => {
        state.voteStatus = payload.status;
        state.loading = "idle";
      })
      .addCase(getVote.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(getConfirmationByMessage.fulfilled, (state, action) => {
        const { audioTrackId } = action.meta.arg;
        if (state.contestByUrl.audio) {
          state.contestByUrl.audio = state.contestByUrl.audio.map((track) => {
            if (track.id === audioTrackId) {
              return { ...track, likes: track.likes + 1, isLiked: true, voices: String(Number(track.voices || '0') + 1) };
            }
            return track;
          });
        }
        if (state.contestByUrl.favorites.audioTracks) {
          state.contestByUrl.favorites.audioTracks = state.contestByUrl.favorites.audioTracks.map((track) => {
            if (track.id === audioTrackId) {
              return { ...track, likes: track.likes + 1, isLiked: true, voices: String(Number(track.voices || '0') + 1) };
            }
            return track;
          });
        }
      });
  },
});

export const selectContestsState = (state: RootState): IInitialState =>
  state.contests;

export const {
  setAccordionState,
  setAccordionIsOpen,
  setDefaultFieldsState,
  setExtraFieldsState,
  setPlaylistButtonState,
  setConfirmationByMessage,
  resetContestByUrl,
} = contestsSlice.actions;

export default contestsSlice.reducer;
